import { isFlowEngineEnabledForProcess, isFlowEngineGloballyEnabled } from './flowEngineConfigHelper';
import { normalizeSpecialistRoleKey } from './activeSpecialistRoleHelper';
import { RolesEnum } from '../types/RolesEnum';
import { TRAY_TAB_MODE, TRAY_VIEW, defaultTrayView, resolveTrayTabMode } from './flowTraySurfaceHelper';

const ROLE_NAME_BY_KEY = {
  'pre-manager': RolesEnum.SPECIALIST_PRE_MANAGER,
  manager: RolesEnum.SPECIALIST_MANAGER,
  validator: RolesEnum.SPECIALIST_VALIDATOR,
  editor: RolesEnum.SPECIALIST_EDITOR,
  coordinator: RolesEnum.SPECIALIST_COORDINATOR
};

const TRAY_ROLE_KEYS = ['pre-manager', 'manager', 'validator', 'editor', 'coordinator'];

const extraFromState = (state = {}) => {
  const extra = state?.flowListFilter?.extra || {};
  return {
    text: extra.text || '',
    from: extra.from || '',
    to: extra.to || '',
    catalogciius_id: extra.catalogciius_id || '',
    catalogserie_id: extra.catalogserie_id || '',
    state_catalog_id: extra.state_catalog_id || '',
    current_user_id: extra.current_user_id || '',
    flow_coverage_group_id: extra.flow_coverage_group_id || '',
    case_phase: extra.case_phase || ''
  };
};

const resolveSelectedRole = (state = {}) => {
  const roleLogin = state?.userRole?.roleLogin;
  return roleLogin?.isRoleSpecialistSelected
    ? roleLogin.roleSpecialistSelected
    : null;
};

export const resolveFlowListRequestParams = (getState, process = null) => {
  const state = typeof getState === 'function' ? getState() : getState;
  const configuration = state?.configuration;
  const extra = extraFromState(state);

  if (!isFlowEngineGloballyEnabled(configuration)) {
    return null;
  }

  if (process && !isFlowEngineEnabledForProcess(configuration, process)) {
    return null;
  }

  const selected = resolveSelectedRole(state);
  const roleKey = normalizeSpecialistRoleKey(selected);
  const tabMode = resolveTrayTabMode(roleKey);
  const trayView = state?.flowListFilter?.trayView || defaultTrayView(roleKey);
  const showRejected = state?.flowListFilter?.showRejected === true;

  if (TRAY_ROLE_KEYS.includes(roleKey)) {
    const flowRole = selected?.name || ROLE_NAME_BY_KEY[roleKey];
    if (!flowRole || !selected?.id) {
      return null;
    }
    const base = {
      flowRole,
      flowRoleId: String(selected.id),
      flowModule: process || undefined,
      showPending: 'false',
      showRejected: roleKey === 'validator' && showRejected ? 'true' : 'false',
      browseAll: false,
      trayView,
      tabMode,
      ...extra,
      catalogserie_id: roleKey === 'pre-manager' ? '' : extra.catalogserie_id
    };

    if (roleKey === 'coordinator') {
      return {
        ...base,
        assignedOnly: 'false',
        showUserFilter: true
      };
    }

    if (tabMode === TRAY_TAB_MODE.THREE && trayView === TRAY_VIEW.POOL) {
      return {
        ...base,
        assignedOnly: 'false',
        claimPool: 'true',
        showUserFilter: false,
        current_user_id: ''
      };
    }
    if (tabMode === TRAY_TAB_MODE.POOL_GENERAL && trayView === TRAY_VIEW.POOL) {
      return {
        ...base,
        assignedOnly: 'false',
        claimPool: 'true',
        showUserFilter: false,
        current_user_id: ''
      };
    }
    if (
      (tabMode === TRAY_TAB_MODE.THREE || tabMode === TRAY_TAB_MODE.POOL_GENERAL)
      && trayView === TRAY_VIEW.GENERAL
    ) {
      return {
        ...base,
        assignedOnly: 'false',
        assigned_scope: 'any',
        showUserFilter: true
      };
    }
    return {
      ...base,
      assignedOnly: 'true',
      showUserFilter: false,
      current_user_id: ''
    };
  }

  return {
    flowModule: process || undefined,
    assignedOnly: 'false',
    showPending: 'false',
    showRejected: 'false',
    browseAll: true,
    showUserFilter: true,
    trayView: TRAY_VIEW.GENERAL,
    tabMode: TRAY_TAB_MODE.GENERAL,
    ...extra
  };
};

export const buildFlowListQueryString = (params) => {
  if (!params) {
    return '';
  }

  const qs = new URLSearchParams();
  if (params.flowRoleId) {
    qs.set('flowRoleId', params.flowRoleId);
  }
  if (params.flowRole) {
    qs.set('flowRole', params.flowRole);
  }
  if (params.flowModule) {
    qs.set('flowModule', params.flowModule);
  }
  qs.set('assignedOnly', params.assignedOnly ?? 'true');
  qs.set('showPending', params.showPending ?? 'false');
  qs.set('showRejected', params.showRejected ?? 'false');
  if (params.claimPool === 'true' || params.claimPool === true) {
    qs.set('claimPool', 'true');
  }
  if (params.assigned_scope) {
    qs.set('assigned_scope', String(params.assigned_scope));
  }
  ['text', 'from', 'to', 'catalogciius_id', 'catalogserie_id', 'state_catalog_id', 'current_user_id', 'flow_coverage_group_id', 'case_phase'].forEach((key) => {
    if (params[key]) {
      qs.set(key, String(params[key]));
    }
  });

  return `?${qs.toString()}`;
};

export const resolveFlowListQueryFromState = (getState, process = null) =>
  buildFlowListQueryString(resolveFlowListRequestParams(getState, process));

export const shouldShowFlowListFilter = (getState, process = null) => {
  const params = resolveFlowListRequestParams(getState, process);
  return Boolean(params && (params.flowRoleId || params.browseAll));
};

export const isFlowModuleListFilterActive = (getState, process = null) =>
  shouldShowFlowListFilter(getState, process);
